import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconBell,
  IconBolt,
  IconBuildingFactory2,
  IconChevronDown,
  IconDeviceDesktop,
  IconGauge,
  IconKey,
  IconLogout,
  IconMoon,
  IconPlugConnected,
  IconShieldLock,
  IconSun,
} from "@tabler/icons-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useOpenAlarmsSummary } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";

type NavLinkSpec = { label: string; to: string; icon: typeof IconBuildingFactory2 };

const NAV_LINKS: NavLinkSpec[] = [
  { label: "Sites", to: "/sites", icon: IconBuildingFactory2 },
  { label: "Alarms", to: "/alarms", icon: IconBell },
  { label: "EV charger commands", to: "/ev-charger-commands", icon: IconBolt },
];

const ADMIN_LINKS: NavLinkSpec[] = [
  { label: "Devices", to: "/admin/devices", icon: IconDeviceDesktop },
  { label: "Meters", to: "/admin/meters", icon: IconGauge },
  { label: "Alarm rules", to: "/admin/alarm-rules", icon: IconAlertTriangle },
  { label: "EV DC Modbus targets", to: "/admin/ev-dc-modbus-targets", icon: IconPlugConnected },
  { label: "Notifications", to: "/admin/notifications", icon: IconBell },
];

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const profile = auth.status === "authenticated" ? auth.profile : null;
  const isAdmin = profile?.roles.includes("admin") ?? false;
  const openAlarmsQuery = useOpenAlarmsSummary();
  const openAlarmCount = openAlarmsQuery.data?.page.total ?? 0;
  const openAlarmBadgeLabel =
    openAlarmCount > 99 ? "99+" : openAlarmCount.toString();

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={3}>EMS</Title>
            {profile?.tenantId && (
              <Badge variant="light" color="indigo" size="sm">
                {profile.tenantId}
              </Badge>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={toggleColorScheme}
              aria-label="Toggle color scheme"
            >
              {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            <Menu shadow="md" position="bottom-end" width={200}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Stack gap={0} align="flex-end">
                      <Text size="sm" fw={500}>
                        {profile?.username ?? "Unknown"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {profile?.email ?? ""}
                      </Text>
                    </Stack>
                    <IconChevronDown size={16} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Roles</Menu.Label>
                {(profile?.roles ?? []).length === 0 ? (
                  <Menu.Item disabled>(none)</Menu.Item>
                ) : (
                  profile!.roles.map((role) => (
                    <Menu.Item key={role} disabled>
                      {role}
                    </Menu.Item>
                  ))
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconKey size={16} />}
                  onClick={() => void auth.changePassword()}
                >
                  Change password
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  color="red"
                  onClick={() => void auth.logout()}
                >
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <AppShell.Section grow component={ScrollArea}>
          {NAV_LINKS.map((link) => {
            const Icon = link.icon;
            const showAlarmBadge = link.to === "/alarms" && openAlarmCount > 0;
            return (
              <NavLink
                key={link.to}
                label={link.label}
                leftSection={<Icon size={18} />}
                rightSection={
                  showAlarmBadge ? (
                    <Badge
                      size="xs"
                      color="red"
                      variant="filled"
                      aria-label={`${openAlarmCount} open alarms`}
                    >
                      {openAlarmBadgeLabel}
                    </Badge>
                  ) : undefined
                }
                active={location.pathname.startsWith(link.to)}
                onClick={() => {
                  navigate(link.to);
                  close();
                }}
              />
            );
          })}
          {isAdmin && (
            <>
              <Divider
                my="sm"
                label={
                  <Group gap={4}>
                    <IconShieldLock size={12} />
                    <Text size="xs">Admin</Text>
                  </Group>
                }
                labelPosition="center"
              />
              {ADMIN_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.to}
                    label={link.label}
                    leftSection={<Icon size={18} />}
                    active={location.pathname.startsWith(link.to)}
                    onClick={() => {
                      navigate(link.to);
                      close();
                    }}
                  />
                );
              })}
            </>
          )}
        </AppShell.Section>
        <AppShell.Section>
          <Text size="xs" c="dimmed">
            Build {import.meta.env.VITE_EMS_BUILD ?? "dev"}
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
